import { Coins } from 'lucide-react'

import { BuyCreditsButton } from './BuyCreditsButton'
import { useCredits } from '@/features/billing/useCredits'

/**
 * "You are out of credits", said once and said properly.
 *
 * Not styled as an error. Nothing broke, nothing was lost, and nothing needs reporting —
 * the account simply cannot pay for this request, which is an ordinary state of an account
 * and reads badly in the red reserved for failures. It uses the same warm, informational
 * treatment as the "GitHub is not connected" banner beside it, for the same reason: it is a
 * condition with an action attached, not a fault.
 *
 * The message comes from the server, which knows the numbers and has already composed them
 * into a sentence ("This needs about 7 credits and you have 2"). Rewriting it here would be
 * two places to keep in step, and the frontend is not the one holding the ledger.
 */
export function OutOfCredits({ message }: { message: string }) {
  const { data, enabled } = useCredits()

  return (
    <div
      role="status"
      data-testid="out-of-credits"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-waiting/20
        bg-waiting-wash px-4 py-2.5 text-sm text-ink-dim"
    >
      <Coins aria-hidden="true" className="h-4 w-4 shrink-0 text-waiting" />
      <span className="min-w-0 flex-1">
        {message}
        {enabled && data ? (
          <span className="text-ink-muted">
            {' '}
            Balance: <span className="tabular-nums text-ink">{data.available_credits}</span>{' '}
            {data.available_credits === 1 ? 'credit' : 'credits'}.
          </span>
        ) : null}
      </span>
      <BuyCreditsButton />
    </div>
  )
}
